<template>
  <UApp :toaster="toaster">
    <NuxtLayout>
      <template #fallback>
        <TradingWorkspaceLoader />
      </template>
      <NuxtLoadingIndicator :height="3" color="primary" />
      <NuxtPage />
    </NuxtLayout>
  </UApp>
</template>

<script lang="ts" setup>
import TradingWorkspaceLoader from '~/components/widgets/TradingWorkspaceLoader.vue'
import type { ToasterProps } from '#ui/components/Toaster.vue'
import { useFavicon } from '@vueuse/core'

const { lang, dir } = useAppDirection()
const { favicon, pageTitle } = useBrand()
const { branding } = storeToRefs(useFeatureStore())
const colorMode = useColorMode()
const toaster = { position: 'bottom-center' } as ToasterProps

useFavicon(favicon)
const primaryColorVariables = [
  '--ui-primary',
  '--ui-primary-600-300',
  '--ui-primary-90',
  '--ui-primary-75',
  '--ui-primary-50',
  '--ui-primary-25',
  '--ui-primary-20',
  '--ui-primary-15',
  '--ui-primary-10'
] as const

function rgba(color: string, opacity: number) {
  const hex = color.replace('#', '')
  const normalized = hex.length === 3 ? hex.split('').map((part) => part + part).join('') : hex

  if (![6, 8].includes(normalized.length) || !/^[\da-f]+$/i.test(normalized)) return null

  const red = Number.parseInt(normalized.slice(0, 2), 16)
  const green = Number.parseInt(normalized.slice(2, 4), 16)
  const blue = Number.parseInt(normalized.slice(4, 6), 16)

  return `rgba(${red}, ${green}, ${blue}, ${opacity})`
}

watchEffect(() => {
  if (!import.meta.client) return

  const root = document.documentElement
  for (const variable of primaryColorVariables) {
    root.style.removeProperty(variable)
  }

  const mode = colorMode.value === 'dark' ? 'dark' : 'light'
  const primary = branding.value?.themes?.[mode]?.primary
  if (typeof primary !== 'string' || !primary.trim()) return

  root.style.setProperty('--ui-primary', primary)
  root.style.setProperty('--ui-primary-600-300', primary)

  for (const [variable, opacity] of [
    ['--ui-primary-90', 0.9],
    ['--ui-primary-75', 0.75],
    ['--ui-primary-50', 0.5],
    ['--ui-primary-25', 0.25],
    ['--ui-primary-20', 0.2],
    ['--ui-primary-15', 0.15],
    ['--ui-primary-10', 0.1]
  ] as const) {
    const value = rgba(primary, opacity)
    if (value) root.style.setProperty(variable, value)
  }
})

useHead({
  title: pageTitle,
  htmlAttrs: {
    lang,
    dir
  }
})
</script>
